// The three INNER PORTS of the import engine — P1 `ParseProvider`,
// P2 `MappingProvider`, P3 `WizardHost`.
//
// Source: `docs/initiatives/new/dashboard-import-tool/import-engine-contract.md` §4
// (the annex ratified as `decisions.md#D-TOOL-BLACKBOX`). The annex reframed the
// upload/map/validate tool as a black box: five outer artifacts and these three
// inner ports. Its central promise is that **swapping an adapter must require
// zero changes outside that adapter** — which is why three construction
// questions (which XLSX library, which mapping engine, whether the wizard is
// forked/scratch/vendor) stopped blocking twenty-plus build issues.
//
// ---------------------------------------------------------------------------
// Obligations EVERY adapter shares (annex §5) — stated once, applying to P1,
// P2, P3, and any future port:
//
//   * **Nothing silently drops** (OVERVIEW Principle 1). Every omission is an
//     explicit, coded, countable value — `SKIP`, `null`, `EXCLUDED` with a
//     reason, a warning code. Absence is NEVER a way to express a decision.
//   * **Nothing writes before COMMIT** (Principle 3). Every port operates on
//     staged data. The one write is the presigned upload.
//   * **Degradation is a first-class outcome, not an exception.** Every port has
//     a defined shape for "I could not fully do my job," and the caller has a
//     defined behavior for it.
//   * **Provenance travels with data** — `engine`, `origin`, `declaredBy`,
//     `basis`, `generatorVersion`. Every inferred value records what inferred
//     it. This is what makes a support question answerable months later and an
//     accuracy regression measurable rather than anecdotal.
//   * **No PII crosses a boundary it does not need to cross** (INV-5).
//     Redaction is structural — see P2 obligation 6.
//
// ---------------------------------------------------------------------------
// CHANGE CONTROL (annex §8). These seams are **FROZEN**. A seam change is a
// SPEC escalation, not a build-time fix: it requires an entry in
// `decisions.md`, an edit to the annex, an edit to the affected SPEC section,
// and a bump of the affected `version` field. Additive changes are cheap and
// ENCOURAGED — a new `ParseWarningCode`, a new optional field. Consumers must
// therefore tolerate unknown codes: render them, count them, never crash on
// them. That tolerance is what keeps additive evolution cheap.

import { z } from 'zod';

import { columnTargetSchema, sourceFileHintSchema, targetFieldKeySchema } from './fields.js';
import { mappingConfidenceTier, type MappingConfidenceTier } from './mapping.js';

// ===========================================================================
// P1 · ParseProvider — bytes → table (annex §4.1)
// ===========================================================================
//
// Behind it: Papa Parse for CSV, and exceljs for XLSX
// (`decisions.md#D-XLSX-LIB`, ratified on supply-chain grounds).
// **This port is why that decision stopped being a blocker.**

/**
 * The eight parse warning codes (annex §4.1). Frozen vocabulary; additive per
 * §8.
 *
 * `DECLARED_RANGE_UNDER_REPORTED` is the important one and the least obvious —
 * see `ParseProvider` obligation 2.
 */
export const PARSE_WARNING_CODES = [
  'ROW_WIDTH_MISMATCH',
  'ENCODING_GUESSED',
  'EMBEDDED_NEWLINE',
  'FORMULA_CELL_UNRESOLVED',
  'ERROR_CELL',
  'MULTI_SHEET_FIRST_USED',
  'TRUNCATED_AT_ROW_LIMIT',
  'DECLARED_RANGE_UNDER_REPORTED',
] as const;

export const parseWarningCodeSchema = z.enum(PARSE_WARNING_CODES);
export type ParseWarningCode = z.infer<typeof parseWarningCodeSchema>;

export const parseWarningSchema = z.object({
  code: parseWarningCodeSchema,
  /** 0-based index into the parsed rows, when the warning is row-scoped. */
  rowIndex: z.number().int().nonnegative().optional(),
  /** 0-based index into `headers`, when the warning is column-scoped. */
  columnIndex: z.number().int().nonnegative().optional(),
  /** Human-readable detail. Display-only — never parsed. */
  detail: z.string(),
});
export type ParseWarning = z.infer<typeof parseWarningSchema>;

export const parsedTableSchema = z.object({
  /**
   * File order, **VERBATIM**. Not trimmed, not case-folded, not de-duplicated
   * (obligation 4). O3's `ColumnMapping.header` is matched against these exact
   * strings, so any normalization here would silently break the mapping join.
   */
  headers: z.array(z.string()),
  /**
   * Count of DATA rows, excluding the header row. Must equal the number of
   * `ROW` lines the validate step later writes into O2 (`mapped.jsonl`), which
   * is in turn the job record's `total`. One number, one meaning.
   */
  rowCount: z.number().int().nonnegative(),
  /**
   * The first `maxPreviewRows` data rows. Every cell is a string or `null` —
   * never absent (obligation 3).
   */
  previewRows: z.array(z.array(z.string().nullable())),
  /** Detected encoding, e.g. `"windows-1252"`. */
  encoding: z.string(),
  /** Set when the adapter transcoded to UTF-8, naming the original encoding. */
  transcodedFrom: z.string().optional(),
  /** XLSX only — which sheet was read (see `MULTI_SHEET_FIRST_USED`). */
  sheetName: z.string().optional(),
  warnings: z.array(parseWarningSchema),
});
export type ParsedTable = z.infer<typeof parsedTableSchema>;

/**
 * The validatable half of a parse request.
 *
 * `source` (the annex's `Blob | ReadableStream`) is deliberately NOT in this
 * schema. It is a runtime handle, not validatable data: `z.instanceof(Blob)`
 * would couple this contracts package to a DOM global, and the whole point of
 * P1 is that BOTH call sites implement it — a browser web worker AND a Lambda
 * (obligation 5). The alternative, `z.any()`, would be a schema that validates
 * nothing while implying it validates something. So the byte source lives on
 * the `ParseRequest` TYPE below and never passes through Zod.
 */
export const parseRequestOptionsSchema = z.object({
  fileName: z.string(),
  declaredMimeType: z.string().optional(),
  maxPreviewRows: z.number().int().positive().default(50),
});

/**
 * CALLER-side options type. Uses `z.input` (not `z.infer`) because
 * `maxPreviewRows` has a `.default(50)`: `z.infer` describes the POST-parse
 * shape, where the default has been applied and the field is therefore
 * REQUIRED — so a caller passing `{ fileName }`, the whole point of a default,
 * would fail to compile. Same reasoning as `JobsListInput` in `index.ts`.
 */
export type ParseRequestOptions = z.input<typeof parseRequestOptionsSchema>;

/** ADAPTER-side, post-parse options: `maxPreviewRows` is guaranteed present. */
export type ParseRequestOptionsParsed = z.infer<typeof parseRequestOptionsSchema>;

/**
 * The full request. `ReadableStream<Uint8Array>` covers the Lambda side (an S3
 * object body) and `Blob` the browser side (a `File` from an input element).
 */
export type ParseRequest = ParseRequestOptions & {
  source: Blob | ReadableStream<Uint8Array>;
};

/**
 * P1 · `ParseProvider` — bytes → table (annex §4.1).
 *
 * **Obligations (binding).** An adapter that violates any of these is
 * non-conformant, and the conformance kit (§7) asserts them:
 *
 * 1. **Row width is invariant.** Every row's cell count equals
 *    `headers.length`. An adapter pads with `null` and emits
 *    `ROW_WIDTH_MISMATCH`, or it fails — it **never** shifts cells left. A
 *    left-shift silently moves every value into the wrong field.
 *
 * 2. **The declared range is never trusted; the true extent is computed from
 *    the cells.** An XLSX `<dimension>` record can under-report the used range,
 *    and a parser that trusts it drops whole columns — **including from the
 *    header row**. That is precisely why obligation 1 cannot catch this on its
 *    own: header and data truncate by the same amount, every row is
 *    consistently narrow, and the file looks internally coherent. An adapter
 *    MUST derive the column extent from the actual cell set, take the wider of
 *    (declared, observed), and emit `DECLARED_RANGE_UNDER_REPORTED` when they
 *    disagree. Verified reproduction and both fixes:
 *    `research/xlsx-parse-safety/README.md` §3.
 *
 * 3. **Every cell is a string or `null`. Never absent.** A formula cell with no
 *    cached value yields `null` + `FORMULA_CELL_UNRESOLVED`; an error cell
 *    (`#REF!`, `#N/A`) yields `null` + `ERROR_CELL`. Both are recoverable and
 *    visible, never a hole. Note that some libraries return a **typed object**
 *    for these (exceljs gives `{ formula, result }` / `{ error }`), so the
 *    adapter must narrow them — a raw `String(cell)` writes the literal text
 *    `[object Object]` into a contact field.
 *
 * 4. **Parse does not mutate values.** No trimming, no case folding, no type
 *    coercion. Normalization belongs to the deterministic transforms (SPEC
 *    §5.3), where it is inspectable and reproducible. A parser that trims is a
 *    parser that can disagree with itself across two call sites.
 *
 * 5. **Both call sites agree.** §4.1 parses browser-side (web worker) *and*
 *    Lambda-side. The same file through both MUST yield identical `headers` and
 *    `rowCount`. Pinned by a conformance fixture (§7).
 *
 * 6. **The parser is a security boundary.** It is the first code to touch a
 *    file uploaded by anyone, so an adapter's library must have no unpatched
 *    high-severity advisory in its parsing path — a ReDoS or
 *    prototype-pollution hole here is directly reachable by any org that can
 *    upload a file. This obligation is what resolved `D-XLSX-LIB`: SheetJS CE's
 *    npm-published version carries two unpatched HIGH advisories with no npm
 *    fix available (`research/xlsx-parse-safety/README.md` §4). Check
 *    `npm audit` on the parsing library as part of CHOOSING or CHANGING an
 *    adapter, not after.
 */
export interface ParseProvider {
  parse(request: ParseRequest): Promise<ParsedTable>;
}

/**
 * Obligation 1 as a callable check: every preview row is exactly as wide as the
 * header row.
 *
 * Deliberately checks `previewRows` only — a `ParsedTable` never carries the
 * full row set (a 50k-row file would defeat the streaming design). Full-file
 * width enforcement is the adapter's own responsibility at parse time; this is
 * the assertion the conformance kit can make from the outside.
 */
export function previewRowWidthsMatchHeaders(table: ParsedTable): boolean {
  return table.previewRows.every((row) => row.length === table.headers.length);
}

// ===========================================================================
// P2 · MappingProvider — headers → proposal (annex §4.2)
// ===========================================================================
//
// Behind it: a fingerprint-only adapter, an LLM adapter (OpenRouter on a pinned
// model per `decisions.md#D-LLM-PROVIDER`), or a vendor's mapper. **This port is
// why the mapping-engine bake-off stopped being a blocker** — and why the
// mapping endpoint can ship with a fingerprint-only adapter while the LLM
// adapter waits on its accuracy re-run.

export const MAPPING_DEGRADED_REASONS = [
  'PROVIDER_UNAVAILABLE',
  'TIMEOUT',
  'RATE_LIMITED',
  'RESPONSE_INVALID',
] as const;

export const mappingDegradedReasonSchema = z.enum(MAPPING_DEGRADED_REASONS);
export type MappingDegradedReason = z.infer<typeof mappingDegradedReasonSchema>;

export const mappingRequestSchema = z.object({
  headers: z.array(z.string()),
  /**
   * At most 5 sample rows, with **emails and phones masked BEFORE the
   * boundary** (INV-5). See obligation 6: the port receives already-redacted
   * samples, so no adapter is ever the component trusted to redact.
   */
  redactedSamples: z.array(z.array(z.string().nullable())).max(5),
  /**
   * The forced-choice catalog. A provider picks from this list or one of the
   * three routing sentinels — never free text (obligation 2).
   */
  fieldCatalog: z.array(targetFieldKeySchema),
  sourceFileHint: sourceFileHintSchema.optional(),
  /** For per-org confirmed-mapping reuse. */
  orgId: z.string(),
});
export type MappingRequest = z.infer<typeof mappingRequestSchema>;

/**
 * The annex's port-facing tier vocabulary (§4.2 obligation 3).
 *
 * ⚠️ **The thresholds are NOT duplicated here.** 0.9 and 0.5 live in exactly
 * one place — `MAPPING_CONFIDENCE_HIGH_THRESHOLD` /
 * `MAPPING_CONFIDENCE_MEDIUM_THRESHOLD` in `mapping.ts` — and everything below
 * is a RENAMING of that single source via `mappingConfidenceTier()`.
 *
 * Why the care: the annex says the confidence→tier mapping belongs to the
 * CONTRACT rather than the provider, because "a provider swap silently moving
 * the auto-accept threshold" is the single most dangerous thing a swap could
 * do. Two independent copies of 0.9 would be exactly that bug waiting to
 * happen — one gets edited, the other doesn't, and columns start auto-accepting
 * at a threshold nobody chose.
 *
 * Two names exist for one concept because SPEC §5.1 names the bands
 * `high`/`medium`/`low` (a display concern — which confidence zone the map
 * step renders a column in) while the annex names them `AUTO`/`REVIEW`/`MANUAL`
 * (a behavior concern — whether a human must touch it). Same boundaries, and
 * the map below is the only bridge between them.
 */
export const PROPOSAL_TIERS = ['AUTO', 'REVIEW', 'MANUAL'] as const;
export const proposalTierSchema = z.enum(PROPOSAL_TIERS);
export type ProposalTier = z.infer<typeof proposalTierSchema>;

/** The one bridge between §5.1's display bands and §4.2's behavior tiers. */
export const PROPOSAL_TIER_BY_CONFIDENCE_TIER: Record<MappingConfidenceTier, ProposalTier> = {
  high: 'AUTO',
  medium: 'REVIEW',
  low: 'MANUAL',
};

/**
 * Tier for a raw confidence score, derived from `mapping.ts`'s banding.
 *
 * Note this does NOT apply the consent cap — use `cappedProposalTier()` for
 * anything target-aware. This function exists for the target-agnostic case and
 * as the single implementation the capped version delegates to.
 */
export function proposalTierFromConfidence(confidence: number): ProposalTier {
  return PROPOSAL_TIER_BY_CONFIDENCE_TIER[mappingConfidenceTier(confidence)];
}

/**
 * The tier-capping rule from annex §4.2 obligation 4: **consent and enum
 * targets are capped at `REVIEW` — never `AUTO`, at any confidence.**
 *
 * Value/enum mapping is the documented sub-80% zone (SPEC Appendix B.3), so the
 * human confirms it regardless of how certain a provider claims to be.
 * Genuinely ambiguous columns route to `CUSTOM_FIELD`, never to a
 * false-confidence consent value — this is the guard against the ImportCSV #15
 * failure mode (a mapper auto-accepting a consent column and silently granting
 * permission nobody gave).
 *
 * It lives in the CONTRACT, not in any adapter, precisely so it survives a
 * provider swap. An adapter cannot opt out of it.
 *
 * A `null` target (the provider could not judge — obligation 1) is `MANUAL`
 * whatever the score, mirroring `mapping.ts`'s `proposalTier()`, which treats
 * an unmapped column as `low` regardless of its number.
 */
export function cappedProposalTier(
  target: z.infer<typeof columnTargetSchema> | null,
  confidence: number,
): ProposalTier {
  if (target === null) return 'MANUAL';

  const tier = proposalTierFromConfidence(confidence);
  if (target === 'emailConsent' || target === 'smsConsent') {
    return tier === 'AUTO' ? 'REVIEW' : tier;
  }
  return tier;
}

export const columnProposalSchema = z.object({
  /**
   * A catalog member, a routing sentinel, or `null`.
   *
   * `null` is REQUIRED rather than optional: a provider that cannot judge a
   * column returns `{ target: null, confidence: 0 }` and never omits the key
   * (obligation 1). Omission would silently vanish a column from the map step —
   * the user would never be asked about it and would never know.
   */
  target: columnTargetSchema.nullable(),
  confidence: z.number().min(0).max(1),
  /**
   * Derived by the CONTRACT via `cappedProposalTier()`, not chosen by the
   * provider (obligation 3 + 4).
   */
  tier: proposalTierSchema,
  /** ≤140 chars. Display-only — NEVER parsed. */
  rationale: z.string().max(140).optional(),
  /**
   * A `TransformRule` (owned by `dataset.ts`, annex O3).
   *
   * Kept structurally loose (`unknown`) rather than imported, to avoid a cycle
   * between this port and the artifact it merely HINTS at: the port suggests a
   * transform, the confirming step (`imports.confirmMapping`) is what validates
   * and persists it into O3. A hint that fails validation is discarded, not a
   * request failure.
   */
  transformRuleHint: z.unknown().optional(),
});
export type ColumnProposal = z.infer<typeof columnProposalSchema>;

export const mappingProposalSchema = z
  .object({
    /**
     * Keyed by the VERBATIM source header. Totality is required: every input
     * header appears exactly once (obligation 1) — assert with
     * `proposalCoversAllHeaders()`.
     */
    proposals: z.record(z.string(), columnProposalSchema),
    detectedPlatform: z.string().nullable().optional(),
    fingerprintMatched: z.boolean(),
    engine: z.object({
      provider: z.string(),
      version: z.string(),
      llmCalled: z.boolean(),
    }),
    /**
     * `true` is a SUCCESS response, not an error (obligation 5). Callers MUST
     * render the manual mapping UI.
     */
    degraded: z.boolean(),
    degradedReason: mappingDegradedReasonSchema.optional(),
  })
  .superRefine((proposal, ctx) => {
    // A degraded response with no reason is unactionable: the operator cannot
    // tell a provider outage from a rate limit from a malformed response, and
    // the funnel's `degradedReason` dimension (O5 `import.mapping_proposed`)
    // silently loses its only interesting axis.
    //
    // The converse is deliberately NOT forbidden — a provider may report a
    // recovered-from reason on a non-degraded response, and rejecting that
    // would turn extra honesty into a failure.
    if (proposal.degraded && proposal.degradedReason === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['degradedReason'],
        message: 'degradedReason is required when degraded is true',
      });
    }
  });
export type MappingProposal = z.infer<typeof mappingProposalSchema>;

/**
 * P2 · `MappingProvider` — headers → proposal (annex §4.2).
 *
 * **Obligations (binding).** 3 and 4 are implemented above as
 * `cappedProposalTier()`; the rest are the adapter's to honor and the
 * conformance kit's to assert:
 *
 * 1. **Total over headers.** Every input header appears exactly once in
 *    `proposals`. A provider that cannot judge a column returns
 *    `target: null, confidence: 0` — it never omits the key.
 * 2. **Forced choice.** `target` is a catalog member or one of the three
 *    routing sentinels. Never free text. An unrecognized value is a contract
 *    violation and is coerced to `null`.
 * 5. **`degraded: true` is a SUCCESS response, not an error.** Callers MUST
 *    render the manual mapping UI. AI assists, never gates (OVERVIEW Principle
 *    2). A provider that THROWS instead of degrading is non-conformant.
 * 6. **Redaction happens on the caller's side of the boundary** (INV-5). The
 *    port receives already-masked samples, so no adapter — including a future
 *    vendor adapter — is ever the component trusted to redact. The invariant is
 *    STRUCTURAL, not behavioral.
 * 7. **No I/O beyond its own inference call.** A `MappingProvider` never
 *    touches DynamoDB, S3, or the job record.
 * 8. **Bounded latency.** Return within the configured timeout or return
 *    `degraded`. The hard ceiling is the ~30s CloudFront origin-response
 *    timeout (SPEC §3.2).
 * 9. **Proposals may be non-deterministic.** Identical requests may yield
 *    different proposals; LLMs are allowed to be LLMs. Therefore **nothing
 *    downstream may assume proposal stability** — the durable artifact is O3,
 *    the human-confirmed config, and re-deriving a mapping at write time is
 *    FORBIDDEN.
 */
export interface MappingProvider {
  propose(request: MappingRequest): Promise<MappingProposal>;
}

/**
 * Obligation 1 as a callable check (§7 asserts it).
 *
 * Checks totality in BOTH directions: every header has a proposal, and the
 * proposal set contains no key that was not a header. A superset is as much a
 * bug as a subset — an extra key means the provider invented a column, and
 * whatever it mapped would be joined against nothing.
 */
export function proposalCoversAllHeaders(
  headers: string[],
  proposal: MappingProposal,
): boolean {
  const proposed = Object.keys(proposal.proposals);
  if (proposed.length !== headers.length) return false;
  return headers.every((header) => header in proposal.proposals);
}

// ===========================================================================
// P3 · WizardHost — the surface's obligations (annex §4.3)
// ===========================================================================
//
// The wizard's construction is undecided and PARKED
// (`decisions.md#D-WIZARD-CONSTRUCTION` is DEFERRED — Milestone D is out of the
// current scope window; it is not blocked, it is not awaiting a nudge).
//
// What IS decided is what the wizard *is*, from the outside: **a client of five
// named procedures, and nothing else.** That is what lets D restart later
// without re-litigating its boundary.

/**
 * The five procedures a wizard may call (annex §4.3), and nothing else.
 *
 * | Procedure                 | Step          | Produces                                        |
 * |---------------------------|---------------|-------------------------------------------------|
 * | `imports.createJob`       | 1 · Upload    | job in STAGED + presigned S3 PUT URL            |
 * | `imports.proposeMapping`  | 2 · Map       | `MappingProposal` (O-adjacent; DISPOSABLE)      |
 * | `imports.confirmMapping`  | 2 · Map       | `MappingConfig` (**O3**) on the job record      |
 * | `imports.validate`        | 3 · Validate  | `mapped.jsonl` (**O2**) + `TriageSummary` (**O4**) |
 * | `imports.commit`          | 4 · Confirm   | attestation recorded, `imports/job.committed` fired |
 *
 * The proposal/config distinction is load-bearing: the proposal is a disposable
 * suggestion, the config is the durable, auditable, reproducible record of what
 * the human confirmed.
 */
export const WIZARD_PROCEDURES = [
  'imports.createJob',
  'imports.proposeMapping',
  'imports.confirmMapping',
  'imports.validate',
  'imports.commit',
] as const;

export const wizardProcedureSchema = z.enum(WIZARD_PROCEDURES);
export type WizardProcedure = z.infer<typeof wizardProcedureSchema>;

/**
 * P3 · `WizardHost` — what any import surface owes, whoever builds it.
 *
 * **Obligations (binding):**
 *
 * 1. **No direct data access.** The wizard writes exactly one thing outside
 *    these five procedures: the source file, via a presigned URL scoped to one
 *    key. No DynamoDB, no S3 SDK, no direct contact writes.
 * 2. **Mountable at a permission-gated dashboard route** (SPEC §2.5), with the
 *    BACKEND re-checking every call regardless of UI gating. UI gating is UX
 *    only.
 * 3. **Emits the O5 funnel events** with the O5 names and payloads (see
 *    `funnel-events.ts`). A vendor iframe that cannot emit them must be wrapped
 *    by a host adapter that does.
 * 4. **A third-party surface sits INSIDE this boundary, behind an adapter** —
 *    see the vendor constraints below.
 * 5. **Resumable.** A Draft job (SPEC §6.1) reopens at the step matching its
 *    status. The wizard holds no state that is not on the job record or in S3 —
 *    closing the tab loses nothing but the current form state.
 *
 * ---------------------------------------------------------------------------
 * **Constraints on a third-party wizard** (annex §4.3a, rewritten per
 * `decisions.md#D-VENDOR-BOUNDARY`). The earlier version banned a *mechanism*
 * rather than protecting an *outcome*, which made the csvbox fallback nearly
 * unusable while another decision still called it available.
 *
 * **HARD — credentials.** A vendor **never** receives a CharityStack Clerk JWT
 * or any AWS credential. Its only write is through a presigned URL scoped to a
 * single S3 key. No exceptions, no vendor-specific accommodation: a JWT inside
 * a third-party iframe can call any dashboard API as that user, and the
 * compliance cost of a scoped presigned PUT is zero.
 *
 * **HARD — four outcomes we own under any vendor:**
 *   * **Consent resolution is ours** (INV-2). A vendor never decides what
 *     Mailchimp `cleaned` means, whether `do_not_email: true` is an opt-out, or
 *     what an empty consent cell implies.
 *   * **Transforms and validation are ours** (SPEC §5.3, §5.4) — the rails'
 *     input has to conform to rules we wrote.
 *   * **We author `mapped.jsonl`** (O2), with `sourceRowNumber`, exclusion
 *     reasons, and consent `basis` intact.
 *   * **Per-row accountability holds** — every excluded row downloadable with a
 *     reason (OVERVIEW Principle 1).
 *
 * **DEFAULT, not prohibition — prefer a mapping over a dataset.** A vendor that
 * returns only the user's confirmed *mapping* is the cleanest fit. But a vendor
 * that returns **rows** is acceptable under three conditions: (a) its rows are
 * consumed as an untrusted `ParsedTable` — the vendor occupies the
 * `ParseProvider` slot (§4.1) and nothing more; (b) our deterministic core runs
 * over them UNCHANGED; (c) `vendorHeaderCrossCheck()` is implemented.
 *
 * **Why the rows path is not the danger it looks like.** Under either shape the
 * vendor must read the file to render a mapping UI, so **PII exposure is
 * identical** — it is driven by whether a vendor touches the file at all, not
 * by what it hands back. (An earlier version of the annex claimed otherwise;
 * that was wrong.) The mechanism prohibition also pointed *away* from the
 * safest vendor architecture: a purely client-side parser never sends the file
 * to a vendor server at all, and its natural output is rows.
 *
 * **What buying never covers, under any shape:** the write path, identity
 * rails, reversal, and consent logic (SPEC §3, §9) are ours.
 */
export interface WizardHost {
  /** The five procedures this host is permitted to call. */
  readonly procedures: readonly WizardProcedure[];
}

export type VendorHeaderCrossCheckResult = {
  ok: boolean;
  ourHeaderCount: number;
  vendorColumnCount: number;
  /** Populated only on failure — display/log-ready. */
  detail?: string;
};

/**
 * Condition (c) of the vendor-rows path (annex §4.3a, §7's P3 bullet).
 *
 * **The one guarantee the rows path genuinely loses** is a column the vendor's
 * parser silently dropped. Recovering it does not need a full re-parse: read
 * the header row from OUR OWN copy of the file in S3 and compare the column
 * count against what the vendor returned. ~20 lines, and it closes the gap.
 *
 * The `baddim.xlsx` fixture (`research/xlsx-parse-safety/`) doubles as the test
 * case here — feed it as "vendor rows" and this check MUST fire.
 *
 * Note the check is `!==`, not `<`. A vendor returning MORE columns than our
 * header row is equally wrong: it means one of us misread the file's extent,
 * and the annex's own P1 obligation 2 is that under-reporting is the failure
 * mode to expect. Either direction is a stop, not a warning.
 */
export function vendorHeaderCrossCheck(input: {
  ourHeaderCount: number;
  vendorColumnCount: number;
}): VendorHeaderCrossCheckResult {
  const { ourHeaderCount, vendorColumnCount } = input;
  if (ourHeaderCount === vendorColumnCount) {
    return { ok: true, ourHeaderCount, vendorColumnCount };
  }
  return {
    ok: false,
    ourHeaderCount,
    vendorColumnCount,
    detail:
      `Vendor returned ${vendorColumnCount} columns but our own read of the ` +
      `file's header row found ${ourHeaderCount}. A column was dropped or ` +
      `invented between the file and the vendor's parse; refusing to proceed.`,
  };
}
